import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff, AlertCircle, Car, Calculator } from "lucide-react";

// ⚠️  CHANGE THESE PASSWORDS BEFORE SHARING WITH ANYONE OUTSIDE THE TEAM
const DEALERSHIP_PASSWORD = "shreemobility2026@dv";   // Dealership panel access
const ACCOUNTING_PASSWORD = "shreemobility2026#av";   // Accounting panel access

const PasswordModal = ({ open, onClose, onSuccess, panelType = "dealership" }) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isDealership = panelType === "dealership";
  const correctPassword = isDealership ? DEALERSHIP_PASSWORD : ACCOUNTING_PASSWORD;
  const panelName = isDealership ? "Dealership Panel" : "Accounting Panel";
  const PanelIcon = isDealership ? Car : Calculator;

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    setTimeout(() => {
      if (password === correctPassword) {
        sessionStorage.setItem("panel", panelType);
        onSuccess();
      } else {
        setError("Incorrect password. Please try again.");
      }
      setLoading(false);
    }, 400);
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setPassword("");
      setError("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className={`p-2 rounded-full ${isDealership ? "bg-primary/10" : "bg-blue-500/10"}`}>
              <PanelIcon className={`h-5 w-5 ${isDealership ? "text-primary" : "text-blue-600"}`} />
            </div>
            <DialogTitle className="font-heading text-xl">{panelName} Access</DialogTitle>
          </div>
          <DialogDescription>
            Enter the {isDealership ? "dealership" : "accounting"} panel password to continue.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="panel-password">Password</Label>
            <div className="relative">
              <Input
                id="panel-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                placeholder={`Enter ${isDealership ? "dealership" : "accounting"} panel password`}
                className="pr-10"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-md px-3 py-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className={`flex-1 ${isDealership ? "bg-primary text-primary-foreground" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
              disabled={loading || !password}
            >
              {loading ? "Verifying..." : "Access Panel"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordModal;
